import {
  Alert,
  Button,
  FormHelperText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Image from "next/image";

import FormControlLabel from "@/components/atoms/form-label/label";

import styles from "./input-file.module.scss";

export type AllowedFileType = {
  extension: string;
  contentType: string;
};

export const allowedFileTypes = [
  { extension: ".pdf", contentType: "application/pdf" },
  { extension: ".doc", contentType: "application/msword" },
  {
    extension: ".docx",
    contentType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  {
    extension: ".xlsx",
    contentType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
  { extension: ".png", contentType: "image/png" },
  { extension: ".jpg", contentType: "image/jpeg" },
  { extension: ".jpeg", contentType: "image/jpeg" },
];

interface Props extends Partial<HTMLInputElement> {
  multiple?: boolean;
  name: string;
  title?: string;
  onChange?: (e: FileList | null) => void;
  label: any;
}

const fileExtensionSeparator = ", ";

export const getAllAllowedFileTypesByFileExtension = (): string => {
  return allowedFileTypes
    .map((key) => key.extension)
    .join(fileExtensionSeparator);
};
export const getAllowedMimeTypes = (): Array<string> => {
  return allowedFileTypes.map((key) => key.contentType);
};

export const getAllowedFileTypes = (): Array<string> => {
  return getAllAllowedFileTypesByFileExtension().split(fileExtensionSeparator);
};

export default function InputFile(props: Readonly<Props>) {
  let { type, onChange: origChange, label, multiple, ...restProps } = props;
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const buttonRef = useRef(null);

  const [hasIndividualFileIsToLargeError, setHasIndividualFileIsToLargeError] =
    useState(false);
  const [hasTotalFileSizeError, setHasTotalFileSizeError] = useState(false);

  const [files, setFiles] = useState<{ files: Array<File> }>({
    files: [],
  });

  const shouldShowTotalFileSizeError = (form: HTMLFormElement): boolean => {
    const thirtyFiveMegaBytes = 35_000_000;
    const inputs = form.querySelectorAll('input[type="file"]');
    let totalSize = 0;

    for (const element of inputs) {
      const input = element as HTMLInputElement;

      if (input.files) {
        let files = Array.from(input.files);

        for (const element of files) {
          const file = element;
          totalSize += file.size;

          if (totalSize > thirtyFiveMegaBytes) {
            return true;
          }
        }
      }
    }
    return false;
  };

  const onChange = (e: SyntheticEvent) => {
    const input: HTMLInputElement = e.target as HTMLInputElement;

    const twoPointFiveMegaBytes = 2_500_000;

    let totalInputFilesSize = 0;
    let foundIndividualFileError = false;
    if (input.files) {
      let files = Array.from(input.files);
      for (const element of files) {
        const file = element;
        totalInputFilesSize += file.size;
        if (file.size > twoPointFiveMegaBytes) {
          foundIndividualFileError = true;
          break;
        }
      }
    }
    if (foundIndividualFileError) {
      setHasIndividualFileIsToLargeError(true);
    } else {
      setHasIndividualFileIsToLargeError(false);
    }

    let foundTotalInputFilesSizeError = shouldShowTotalFileSizeError(
      input.closest("form") as HTMLFormElement,
    );
    if (foundTotalInputFilesSizeError) {
      setHasTotalFileSizeError(true);
    } else {
      setHasTotalFileSizeError(false);
    }

    if (
      input.files &&
      !foundIndividualFileError &&
      !foundTotalInputFilesSizeError
    ) {
      setFiles({ files: Array.from(input.files) });
    } else {
      setFiles({ files: [] });
    }
    origChange?.(input.files);
  };

  const onRemoveClick = (index: number) => {
    const dt = new DataTransfer();
    const input: HTMLInputElement =
      inputRef.current as unknown as HTMLInputElement;
    const { files } = input;
    if (!files) {
      return;
    }
    for (let i = 0; i < files.length; i++) {
      const file: File = files[i];
      if (index !== i) dt.items.add(file);
    }
    input.files = dt.files;
    setFiles({ files: Array.from(input.files) });
    origChange?.(input.files);
  };

  useEffect(() => {
    const form = (inputRef.current as unknown as HTMLInputElement).closest(
      "form",
    );

    const onSubmit = (e: SubmitEvent) => {
      if (form?.checkValidity()) {
        if (hasTotalFileSizeError || hasIndividualFileIsToLargeError) {
          e.preventDefault();
          e.stopPropagation();
          (buttonRef.current as unknown as HTMLButtonElement)?.focus();
        }
      }
    };

    form?.removeEventListener("submit", onSubmit);
    form?.addEventListener("submit", onSubmit);

    return () => {
      form?.removeEventListener("submit", onSubmit);
    };
  });

  if (multiple) {
    restProps = {
      ...restProps,
      // @ts-ignore
      multiple,
    };
  }

  return (
    <>
      <FormControlLabel
        labelPlacement="top"
        control={
          <Button
            component="span"
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
            ref={buttonRef}
          >
            {/* @ts-ignore */}
            <input
              ref={inputRef}
              type="file"
              aria-describedby="my-helper-text"
              className={`sr-only`}
              onChange={onChange}
              accept={getAllAllowedFileTypesByFileExtension()}
              {...restProps}
            />
            <UploadFileIcon />
            {multiple ? t("Attach file(s)") : t("Attach file")}
          </Button>
        }
        label={label}
      />

      {multiple ? (
        <Alert
          sx={{ display: { xs: "none", lg: "block", mb: 1, mt: 1 } }}
          severity="info"
        >
          <Stack gap={1} direction={"row"}>
            {t(
              "In order to upload multiple files from your computer; use shift, arrow down or up or control (PC) or command (Mac) keys on the keyboard.",
            )}
            <Image
              src="/images/pc-select-multiple-files-with-mouse.jpg"
              width="221"
              height="81"
              alt={t("pc, select multiple files with mouse")}
              title={
                t("pc, select multiple files with mouse") ||
                "pc, select multiple files with mouse"
              }
            />
          </Stack>
        </Alert>
      ) : null}
      {hasIndividualFileIsToLargeError ? (
        <Alert sx={{ mb: 1, mt: 1 }} severity="error">
          {t("File size can not exceed 2.5 MB")}
        </Alert>
      ) : null}
      {hasTotalFileSizeError ? (
        <Alert sx={{ mb: 1, mt: 1 }} severity="error">
          {t("The files attached to this form must not exceed 35 MB in total")}
        </Alert>
      ) : null}

      <FormHelperText id="my-helper-text">
        {t("Supported file types: {{types}}", {
          types: getAllAllowedFileTypesByFileExtension(),
        })}
      </FormHelperText>
      {files.files.length ? (
        <TableContainer>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell>{t("Remove")}</TableCell>
                <TableCell>{t("File")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.files.map((file, index) => {
                return (
                  <TableRow key={file.name}>
                    <TableCell>
                      <button
                        type="button"
                        className={styles.removeBtn}
                        title={`${t("Remove")} ${file.name}`}
                        onClick={() => onRemoveClick(index)}
                      >
                        <span className="sr-only">
                          {t("Remove")} {file.name}
                        </span>
                        <span aria-hidden="true">
                          <DeleteIcon />
                        </span>
                      </button>
                    </TableCell>
                    <TableCell style={{ wordBreak: "break-word" }}>
                      {file.name}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </>
  );
}
